import React, { useState } from "react";
import styles from "./EmailValidation.module.scss";
import Input from "../Forms/Input/Input";
import Button from "../Forms/Button/Button";
import { motion } from "framer-motion";
import { fireAnalytics } from "../../../utils/firebase.js";
import { EmailValidationProps } from "../../../types/register.ts";
import { useGoogleLogin, TokenResponse } from "@react-oauth/google";
import { useDispatch, useSelector } from "react-redux";
import { createPreRegister } from "../../../store/actions";
import { AlertCircle, X } from "lucide-react";
import { useNavigate } from "react-router-dom";

const formVariant = {
  hidden: {
    opacity: 0,
    y: 30,
    scale: 0.95,
  },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.6,
      ease: [0.25, 0.46, 0.45, 0.94],
      staggerChildren: 0.1,
    },
  },
};

const itemVariant = {
  hidden: {
    opacity: 0,
    y: 20,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: [0.25, 0.46, 0.45, 0.94],
    },
  },
};

const EmailValidation: React.FC<EmailValidationProps> = ({
  onEmailSubmit,
  onGoogleSuccess,
  isLoading,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isPreRegistering = useSelector((state: any) => state.register?.isPreRegistering || false);

  const [email, setEmail] = useState<string>("");
  const [emailError, setEmailError] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [showErrorToast, setShowErrorToast] = useState<boolean>(false);

  // Hook de Google Login
  const login = useGoogleLogin({
    onSuccess: (tokenResponse: TokenResponse) => {

      fireAnalytics("registro_google_iniciado", { category: "usuario" });
      if (onGoogleSuccess && tokenResponse.access_token) {
        onGoogleSuccess(tokenResponse.access_token);
      }
    },
    onError: () => {
      fireAnalytics("registro_google_error", { category: "usuario" });
    },
    scope: 'openid email profile',
  });

  const handleGoogleClick = () => {
    login();
  };

  const validateEmail = (email: string): boolean => {

    if (!email) {
      setEmailError("El email es obligatorio");
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setEmailError("Ingresa un email válido");
      return false;
    }

    setEmailError("");
    return true;
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const value = e.target.value;
    setEmail(value);

    if (emailError) {
      setEmailError("");
    }
  };

  const handleEmailBlur = (): void => {
    if (email) {
      validateEmail(email);
    }
  };

  // Función para detectar si el error es de usuario ya pre-registrado (no completado)
  const isUserAlreadyPreRegisteredError = (error: string): boolean => {
    return error.toLowerCase().includes("el usuario ya está pre-registrado") ||
      error.toLowerCase().includes("ya está pre-registrado") ||
      error.toLowerCase().includes("already pre-registered");
  };

  // Función para detectar si el usuario ya está completamente registrado (debe ir al login)
  const isUserAlreadyRegisteredError = (error: string): boolean => {
    return error.toLowerCase().includes("el usuario ya está registrado") && 
           !error.toLowerCase().includes("pre-registrado");
  };


  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();

    if (!validateEmail(email)) {
      return;
    }

    setIsSubmitting(true);
    setShowErrorToast(false);

    try {
      const result = await dispatch(createPreRegister(email) as any);

      // Si llegamos aquí, el pre-registro fue exitoso (nuevo usuario)
      if (onEmailSubmit) {
        onEmailSubmit({ email, isNewUser: true });
      }
    } catch (error) {
      const originalMessage = error?.response?.data?.message || "";
      const responseData = error?.response?.data;

      // CASO 1: Usuario ya está completamente registrado (debe ir al login)
      if (originalMessage === "El usuario ya está registrado" && responseData?.validated !== false) {
        setShowErrorToast(true);
        setTimeout(() => {
          setShowErrorToast(false);
        }, 5000);
        return;
      }

      // CASO 1B: Usuario registrado pero no validado (cuenta vieja sin onboarding)
      if (originalMessage === "El usuario ya está registrado" && responseData?.validated === false) {
        // Usuario registrado pero no validado → reenviar código y continuar
        if (onEmailSubmit) {
          onEmailSubmit({ email, isPreRegistered: true });
        }
        return;
      }

      // CASO 2: Usuario ya está pre-registrado (puede continuar el proceso)
      if (originalMessage.includes("pre-registrado") || originalMessage.includes("already pre-registered")) {
        // Verificar si ya está validado para saltar CodeValidation
        if (responseData?.validated === true) {
          // Usuario pre-registrado y validado → ir directamente a PasswordSetup
          if (onEmailSubmit) {
            onEmailSubmit({ email, isPreRegistered: true, isAlreadyValidated: true });
          }
        } else {
          // Usuario pre-registrado pero no validado → ir a CodeValidation
          if (onEmailSubmit) {
            onEmailSubmit({ email, isPreRegistered: true });
          }
        }
        return;
      }

      // CASO 3: Otros errores (mostrar toast genérico)
      setShowErrorToast(true);
      setTimeout(() => {
        setShowErrorToast(false);
      }, 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseToast = (): void => {
    setShowErrorToast(false);
  };

  // Función para manejar el click en el enlace de login
  const handleLoginClick = (): void => {
    fireAnalytics("error_to_login_click", { category: "usuario" });
    navigate("/");
  };

  return (
    <motion.div
      variants={formVariant}
      initial="hidden"
      animate="visible"
      className={styles.container}
    >
      <motion.div variants={itemVariant} className={styles.content}>
        <motion.p
          variants={itemVariant}
          className={styles.subtitle}
        >
          Ingresa tu email y te enviaremos un código para continuar
        </motion.p>

        <motion.form variants={itemVariant} className={styles.form} onSubmit={handleSubmit}>
          <div className={styles.formGroup}>
            <Input
              field={{
                type: "email",
                placeholder: "tu@email.com",
                value: email,
                onChange: handleEmailChange,
                onBlur: handleEmailBlur
              }}
              label="Email"
              error={emailError}
              handleKey={() => { }}
            />
          </div>

          <Button
            type="button"
            onClick={handleSubmit}
            disabled={isPreRegistering || isLoading || !email || !!emailError}
            className={styles.btn}
            colored
            bordered={false}
            link={false}
            typebtn="primary"
          >
            {isLoading || isPreRegistering ? "Enviando..." : "Enviar código"}
          </Button>
        </motion.form>

                 {/* Toast de error que no ocupa espacio */}
         {showErrorToast && (
          <motion.div
            initial={{ opacity: 0, y: -20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -20, scale: 0.95 }}
            className={styles.errorToast}
          >
            <div className={styles.errorToast_content}>
              <AlertCircle size={20} className={styles.errorToast_icon} />
                             <div className={styles.errorToast_text}>
                 <span>
                   Ya contamos con ese email registrado. Por favor, ve al{" "}
                   <a
                     href="/"
                     onClick={handleLoginClick}
                     className={styles.errorToast_loginLink}
                     style={{ cursor: 'pointer', textDecoration: 'underline' }}
                   >
                     login
                   </a>
                   {" "}para acceder a tu cuenta.
                 </span>
               </div>
              <button
                onClick={handleCloseToast}
                className={styles.errorToast_close}
                type="button"
              >
                <X size={16} />
              </button>
            </div>
          </motion.div>
        )}

        {/* Divider y botón de Google personalizado */}
        <motion.div variants={itemVariant} className={styles.googleDivider}>
          <span className={styles.googleDivider_line}></span>
          <div className={styles.googleDivider_text}>O continúa con</div>
          <span className={styles.googleDivider_line}></span>
        </motion.div>

        <motion.button
          variants={itemVariant}
          className={styles.googleButton}
          onClick={handleGoogleClick}
          disabled={isLoading}
          type="button"
        >
          <div className={styles.googleButton_content}>
            <svg
              className={styles.googleButton_icon}
              viewBox="0 0 24 24"
              width="24"
              height="24"
            >
              <path
                fill="#234ba2"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="#2d5bc0"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="#eef3ff"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="#1e3d8a"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            <span className={styles.googleButton_text}>
              Continuar con Google
            </span>
          </div>
        </motion.button>
      </motion.div>
    </motion.div>
  );
};

export default EmailValidation;
